/**
 * Adapters for the shared <AgentNew scope> component (PR-B).
 *
 * Each adapter implements `AgentNewAdapter` from `./AgentNew.types` —
 * its job is to smooth over the scope-conditional mutation hooks, save
 * payload shapes, navigation targets, and copy strings so the shared
 * component body sees ONE normalized contract regardless of scope.
 *
 * See AgentNew.tsx for the canonical scope-deviation table.
 */
import type { CreateAgentInput } from '@/lib/use-agents';
import { useCreateAgent } from '@/lib/use-agents';
import type { CreateCatalogAgentInput } from '@/lib/use-sysadmin-catalog-agents';
import { useCreateCatalogAgent } from '@/lib/use-sysadmin-catalog-agents';
import {
  buildTenantQuickBody,
  buildSysadminQuickBody,
  parseTenantImportPayload,
  parseSysadminImportPayload,
} from './AgentNew.helpers';
import type {
  AgentNewAdapter,
  AgentNewScope,
  QuickCreateValues,
  SaveAgentArgs,
} from './AgentNew.types';

// ─── Sysadmin save response shape (the catalog endpoint returns `{ data }`)─

type CatalogCreateResp = { data: { id: string } };

// ─── Tenant adapter ─────────────────────────────────────────────────────────

const TENANT_IMPORT_PLACEHOLDER =
  '{\n  "name": "My Agent",\n  "description": "...",\n  "systemPrompt": "...",\n  "tools": [...],\n  "skills": [...]\n}';

export const tenantAdapter: AgentNewAdapter = Object.freeze({
  pageTitle: 'Create Agent',
  pageSubtitle: 'Choose how you want to create your agent',
  backButtonTitle: 'Back to Agents',
  backRoute: '/agents',
  builderRoute: '/agents/builder',
  // Tenant builder route only accepts `?model=`; passing `search:
  // undefined` keeps TanStack from emitting an empty query string.
  builderSearch: undefined,

  quickCreateCopy:
    'Fill out a simple form with name, system prompt, and basic configuration. No wizard, no conversation.',
  importPlaceholder: TENANT_IMPORT_PLACEHOLDER,
  showSlugField: false,

  buildQuickCreateBody(values: QuickCreateValues): CreateAgentInput {
    return buildTenantQuickBody(values);
  },

  buildImportBody(raw: string) {
    const body = parseTenantImportPayload(raw);
    if (!body) return null;
    return { body, agentName: body.name };
  },

  useSaveAgent() {
    const createAgent = useCreateAgent();
    return {
      saveInFlight: createAgent.isPending,
      save({ body, onSuccess, onError }: SaveAgentArgs) {
        createAgent.mutate(body as CreateAgentInput, {
          // useCreateAgent's ServerAgent shape is internal to use-agents
          // and not re-exported. The shared body only uses the response
          // through `adapter.navigateAfterSave`, which tenant ignores.
          onSuccess: (resp) => onSuccess(resp),
          onError,
        });
      },
    };
  },

  navigateAfterSave() {
    // Tenant returns to the list view after create.
    return { to: '/agents' };
  },
});

// ─── Sysadmin adapter ───────────────────────────────────────────────────────

const SYSADMIN_IMPORT_PLACEHOLDER = `{
  "name": "My Agent",
  "slug": "my-agent",
  "description": "...",
  "systemPrompt": "...",
  "category": "custom",
  "tools": [],
  "functions": [],
  "skills": [],
  "workflows": []
}`;

export const sysadminAdapter: AgentNewAdapter = Object.freeze({
  pageTitle: 'Create System Agent',
  pageSubtitle: 'Choose how you want to create your agent',
  backButtonTitle: 'Back to agent catalog',
  backRoute: '/sysadmin/catalog/agents',
  builderRoute: '/sysadmin/catalog/agents/builder',
  // Sysadmin builder route validates `?id=` AND `?model=`. Explicitly
  // reset BOTH so navigation from "Build with AI" never inherits stray
  // search params.
  builderSearch: { id: undefined, model: undefined },

  quickCreateCopy:
    'Fill out a simple form with name, slug, system prompt, and basic configuration. No wizard, no conversation.',
  importPlaceholder: SYSADMIN_IMPORT_PLACEHOLDER,
  showSlugField: true,

  buildQuickCreateBody(values: QuickCreateValues): CreateCatalogAgentInput {
    return buildSysadminQuickBody(values);
  },

  buildImportBody(raw: string) {
    const body = parseSysadminImportPayload(raw);
    if (!body) return null;
    return { body, agentName: body.displayName };
  },

  useSaveAgent() {
    const createAgent = useCreateCatalogAgent();
    return {
      saveInFlight: createAgent.isPending,
      save({ body, onSuccess, onError }: SaveAgentArgs) {
        createAgent.mutate(body as CreateCatalogAgentInput, {
          onSuccess: (resp) => onSuccess(resp),
          onError,
        });
      },
    };
  },

  navigateAfterSave(resp: unknown) {
    // Sysadmin sends the user straight into the builder editor for the
    // newly-created agent — the create flow is the entry to authoring
    // its capabilities (tools, skills, prompts).
    const id = (resp as CatalogCreateResp | undefined)?.data?.id;
    return {
      to: '/sysadmin/catalog/agents/builder',
      search: { id, model: undefined },
    };
  },
});

// ─── Adapter selector ───────────────────────────────────────────────────────

export function getAdapter(scope: AgentNewScope): AgentNewAdapter {
  return scope === 'sysadmin' ? sysadminAdapter : tenantAdapter;
}
